import { useCurrentLanguage } from '@co4cloud/base';
import { useDebouncedCallback, useOnClickOutside } from '@co4cloud/components';
import { SlDropdown as DropdownEl } from '@shoelace-style/shoelace';
import {
  SlButton,
  SlCard,
  SlDialog,
  SlDropdown,
  SlIcon,
  SlIconButton,
  SlTooltip,
} from '@shoelace-style/shoelace/dist/react';
import {
  add,
  endOfDay,
  format,
  intervalToDuration,
  isAfter,
  isBefore,
  isSameMonth,
  startOfDay,
  startOfMonth,
  sub,
} from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Range, DateRangePicker as ReactDateRangePicker } from 'react-date-range';
import { useTranslation } from 'react-i18next';
import { DateTextInput } from './DateTextInput';
import { createStaticRanges, getDefineds, localeDateFormat, RDR_LOCALE_KEY, zonedNow } from './ranges';

import * as locales from 'react-date-range/dist/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface DatePickerProps {
  dates: {
    start: number | Date;
    end: number | Date;
  };
  /**
   * IANA zone whose calendar days to show, e.g. the site's timezone. Presets and
   * the default ceiling follow it, and `onSave` reports wall-clock times in it —
   * convert to instants before querying (see `convertToTimezone`).
   * @default the browser's timezone
   */
  timezone?: string;
  /**
   * Disable dates before this date. Provide a date object or a timestamp.
   * @default undefined
   */
  min?: Date | number;
  /**
   * Disable dates after this date. Provide a date object or a timestamp. Prefer
   * `timezone` instead: a caller-computed ceiling is only as fresh as the caller's
   * last render, so it can cap the picker at a stale day (CO4-3535).
   * @default end of today, in `timezone`
   */
  max?: Date | number;
  /**
   * @param dates returns the start and end dates of the selected range as timestamps
   */
  onSave: (dates: DatePickerProps['dates']) => void;
}

const isValidDate = (date: Date) => date instanceof Date && !isNaN(date.getTime());
const getDefaultDates = (timezone?: string) => {
  const { startOfToday, endOfToday } = getDefineds(zonedNow(timezone));
  return { start: startOfToday, end: endOfToday };
};
const MOBILE_BREAKPOINT = '(max-width: 767px)';

export const DateRangePicker: React.FC<DatePickerProps> = (props) => {
  const { onSave, max, min, timezone } = props;
  const { dates = getDefaultDates(timezone) } = props;

  const dateObjs = useMemo(() => ({ start: startOfDay(dates.start), end: endOfDay(dates.end) }), [dates]);

  const lang = useCurrentLanguage();
  // `react-date-range`'s locale export wraps each locale under its own key again
  // (`locales.de = { de: <real locale> }`), and has no `en` key at all (only
  // enUS/enGB) - mapped explicitly and double-indexed so this is always the real
  // date-fns locale object, never the wrapper or undefined.
  const rdrLocaleKey = RDR_LOCALE_KEY[lang] ?? 'enUS';
  const locale = locales[rdrLocaleKey]?.[rdrLocaleKey];
  const [t] = useTranslation();
  const [showPicker, setShowPicker] = useState(false);
  const [range, setRange] = useState({ ...dateObjs });
  const [error, setError] = useState('');
  // Tracks whether each typed box currently holds a complete, parseable date -
  // separate from `range` so Apply can disable live, per keystroke (CO4-3594 AC),
  // without the calendar/range reacting on every keystroke too.
  const [fieldsComplete, setFieldsComplete] = useState({ start: true, end: true });
  // Set on blur/Enter, not on every keystroke - a field only turns red once the
  // user has left it incomplete, not while they're still mid-typing it.
  const [fieldsTouched, setFieldsTouched] = useState({ start: false, end: false });
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_BREAKPOINT).matches : false,
  );
  const dropdownRef = useRef<DropdownEl>(null);
  const rangeRef = useRef({ ...dateObjs });
  // Which month the calendar is currently centered on, so a typed date in a
  // different month can force it to re-center (see `commitField` - the library's
  // own `shownDate` prop is only read once, before first mount).
  const shownMonthRef = useRef(startOfMonth(dateObjs.start));
  const [calendarKey, setCalendarKey] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_BREAKPOINT);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // `range` and `rangeRef` are otherwise only seeded on mount, so a `dates` change
  // from the parent — a site's timezone arriving, say — never reached the label or
  // the grid. Deps are primitives: an omitted `dates` prop is a new object per render.
  const datesStart = dateObjs.start.valueOf();
  const datesEnd = dateObjs.end.valueOf();

  useEffect(() => {
    rangeRef.current = { start: dateObjs.start, end: dateObjs.end };
    setRange({ start: dateObjs.start, end: dateObjs.end });
    setFieldsComplete({ start: true, end: true });
    setFieldsTouched({ start: false, end: false });
    shownMonthRef.current = startOfMonth(dateObjs.start);
  }, [datesStart, datesEnd]);

  // `showPicker` in the deps so "today" is re-evaluated on each open (CO4-3535).
  const staticRanges = useMemo(() => {
    const defineds = getDefineds(zonedNow(timezone));

    return createStaticRanges([
      { label: t('today'), range: () => ({ startDate: defineds.startOfToday, endDate: defineds.endOfToday }) },
      {
        label: t('yesterday'),
        range: () => ({ startDate: defineds.startOfYesterday, endDate: defineds.endOfYesterday }),
      },
      { label: t('thisWeek'), range: () => ({ startDate: defineds.startOfWeek, endDate: defineds.endOfToday }) },
      {
        label: t('lastWeek'),
        range: () => ({ startDate: defineds.startOfLastWeek, endDate: defineds.endOfLastWeek }),
      },
      { label: t('thisMonth'), range: () => ({ startDate: defineds.startOfMonth, endDate: defineds.endOfToday }) },
      {
        label: t('lastMonth'),
        range: () => ({ startDate: defineds.startOfLastMonth, endDate: defineds.endOfLastMonth }),
      },
      {
        label: t('last3Months'),
        range: () => ({ startDate: defineds.startOfLast3Months, endDate: defineds.endOfLast3Months }),
      },
      { label: t('thisYear'), range: () => ({ startDate: defineds.startOfThisYear, endDate: defineds.endOfToday }) },
      {
        label: t('lastYear'),
        range: () => ({ startDate: defineds.startOfLastYear, endDate: defineds.endOfLastYear }),
      },
    ]);
  }, [showPicker, t, timezone]);

  const close = () => {
    setRange({ ...dateObjs });
    setError('');
    setFieldsComplete({ start: true, end: true });
    setFieldsTouched({ start: false, end: false });
    setShowPicker(false);
  };

  useOnClickOutside(dropdownRef, close);

  const before = useMemo(() => (min ? new Date(min) : undefined), [min]);
  // Re-evaluated on open too: opening the picker does not re-render the caller.
  const after = useMemo(
    () => (max ? new Date(max) : getDefineds(zonedNow(timezone)).endOfToday),
    [max, showPicker, timezone],
  );

  const checkDatesValidity = useCallback(
    (dates: typeof range) => {
      let areValid = true,
        msg = '';
      const { start, end } = dates;
      if (!isValidDate(new Date(start)) || !isValidDate(new Date(end)) || start.valueOf() > end.valueOf()) {
        msg = t('startBeforeEndError');
        areValid = false;
      } else if ((before && isBefore(start, before)) || (after && isAfter(end, after))) {
        msg = t('dateRangeError');
        areValid = false;
      }
      setError(msg);
      return areValid;
    },
    [before, after],
  );

  // Typed-input commit (blur/Enter only - not per keystroke, so the calendar
  // doesn't jump while the user is still typing). Merges into the *other* field's
  // current draft, same as a calendar click would, and re-centers the calendar on
  // the edited month via `calendarKey` since `shownDate` can't do that after mount.
  const commitField = (field: 'start' | 'end', date: Date | null) => {
    setFieldsTouched((f) => ({ ...f, [field]: true }));
    if (!date) return;
    const next = { ...range, [field]: field === 'start' ? startOfDay(date) : endOfDay(date) };
    setRange(next);
    checkDatesValidity(next);

    const month = startOfMonth(date);
    if (!isSameMonth(month, shownMonthRef.current)) {
      shownMonthRef.current = month;
      setCalendarKey((k) => k + 1);
    }
  };

  // Stable across renders (setFieldsComplete is a state setter, guaranteed stable)
  // so DateTextInput's own onAccept memoization isn't defeated on every keystroke -
  // an inline arrow here would recreate `onLiveChange` on every render, which
  // cascades into `useIMask` reconfiguring the mask mid-type (see DateTextInput.tsx).
  const onStartLiveChange = useCallback(
    (complete: boolean) => setFieldsComplete((f) => ({ ...f, start: complete })),
    [],
  );
  const onEndLiveChange = useCallback((complete: boolean) => setFieldsComplete((f) => ({ ...f, end: complete })), []);

  const onDatesApply = useDebouncedCallback((dates: typeof range) => {
    if (checkDatesValidity(dates)) {
      setShowPicker(false);
      const start = startOfDay(dates?.start);
      const end = endOfDay(dates?.end);
      rangeRef.current = { start, end };
      onSave({ start: start?.valueOf(), end: end?.valueOf() });
    }
  });

  const updateDates = useDebouncedCallback((dates: typeof range) => {
    setRange(dates);
  });

  const moveRange = (direction: 'forward' | 'backward', dates: typeof range) => {
    const start = startOfDay(dates.start),
      end = endOfDay(dates.end);
    const intervals = intervalToDuration({ start, end: add(end, { seconds: 1 }) });
    const newStartDate = direction === 'forward' ? add(start, intervals) : sub(start, intervals);
    const newEndDate = direction === 'forward' ? add(end, intervals) : sub(end, intervals);
    const newRange = { start: newStartDate, end: newEndDate };
    rangeRef.current = { ...newRange };
    updateDates(newRange);
    onDatesApply(newRange);
  };

  const formats = localeDateFormat(lang);

  const calendar = (
    <>
      <div className="date-range-inputs">
        <DateTextInput
          value={range.start}
          pattern={formats.mask}
          displayFormat={formats.display}
          locale={locale}
          ariaLabel={t('startDate')}
          invalid={(fieldsTouched.start && !fieldsComplete.start) || !!error}
          onLiveChange={onStartLiveChange}
          onCommit={(date) => commitField('start', date)}
        />
        <span className="date-range-inputs__sep">–</span>
        <DateTextInput
          value={range.end}
          pattern={formats.mask}
          displayFormat={formats.display}
          locale={locale}
          ariaLabel={t('endDate')}
          invalid={(fieldsTouched.end && !fieldsComplete.end) || !!error}
          onLiveChange={onEndLiveChange}
          onCommit={(date) => commitField('end', date)}
        />
      </div>
      <ReactDateRangePicker
        key={calendarKey}
        inputRanges={[]}
        weekStartsOn={1}
        showDateDisplay={false}
        shownDate={shownMonthRef.current}
        direction="horizontal"
        rangeColors={['#377289']}
        staticRanges={staticRanges}
        ranges={[{ startDate: range.start, endDate: range.end, key: 'selection' }] as Range[]}
        onChange={({ selection }) => {
          setRange({ start: selection.startDate, end: selection.endDate });
          setFieldsComplete({ start: true, end: true });
          setFieldsTouched({ start: false, end: false });
        }}
        locale={locale}
        minDate={before}
        maxDate={after}
      />
    </>
  );

  const pickerActions = (className = 'picker-actions') => (
    <div className={className}>
      {!!error && (
        <SlTooltip content={error}>
          <SlIcon name="exclamation-triangle-fill" />
        </SlTooltip>
      )}
      <SlButton variant="neutral" size="small" onClick={close}>
        {t('close')}
      </SlButton>
      <SlButton
        variant="primary"
        size="small"
        disabled={!fieldsComplete.start || !fieldsComplete.end || !!error}
        onClick={() => onDatesApply(range)}>
        {t('apply')}
      </SlButton>
    </div>
  );

  const triggerLabel = `${format(rangeRef?.current?.start, formats.display)} - ${format(
    rangeRef?.current?.end,
    formats.display,
  )}`;

  return (
    <div className="date-range-picker">
      <SlIconButton name="chevron-left" library="lucide" onClick={() => moveRange('backward', rangeRef?.current)} />

      {isMobile ? (
        <>
          <div
            className="date-range"
            aria-invalid={!!error || undefined}
            onClick={() => setShowPicker((prev) => !prev)}>
            {triggerLabel}
          </div>
          <SlDialog className="date-range-dialog" open={showPicker} noHeader onSlAfterHide={close}>
            <div className="date-range-dialog__scroll">{calendar}</div>
            {pickerActions('picker-actions picker-actions--sticky')}
          </SlDialog>
        </>
      ) : (
        <SlDropdown hoist open={showPicker} ref={dropdownRef}>
          <div
            slot="trigger"
            className="date-range"
            aria-invalid={!!error || undefined}
            onClick={() => setShowPicker((prev) => !prev)}>
            {triggerLabel}
          </div>
          <SlCard className="date-pickers-card">
            {calendar}
            {pickerActions()}
          </SlCard>
        </SlDropdown>
      )}

      <SlIconButton name="chevron-right" library="lucide" onClick={() => moveRange('forward', rangeRef?.current)} />
    </div>
  );
};
